import { AuthTokens } from '@/features/Auth/auth.types'
import { clearAuthStorage, getUser, saveUser } from '@/features/Auth/auth.utils'
import { ApiRoutes, AppRoutes } from '@/routes'
import { createDeviceInfo } from '@/shared/helpers/device'
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

export const axiosClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  timeout: 60000,
})

export const AXIOS_ERROR_HANDLER_MAP: Record<
  number,
  (error: AxiosError<any>) => void
> = {
  404: () => {
    console.info('--404 error: Not Found')
  },
}

let requestsAbortController = new AbortController()

export function cancelPendingRequests() {
  requestsAbortController.abort()
  requestsAbortController = new AbortController()
}

axiosClient.interceptors.request.use(
  async (request) => {
    if (typeof window === 'undefined') return request

    request.signal = requestsAbortController.signal

    const isPubRequest = /\/pub(\/|$)/.test(request.url ?? '')

    if (!isPubRequest) {
      const user = getUser()
      if (!user) return Promise.reject(new Error('No auth session - request cancelled'))
      request.headers.Authorization = `Bearer ${user.accessToken}`
    }

    if (isPubRequest) {
      try {
        const device = createDeviceInfo()
        request.headers['Go-Defi-Device-Id'] = device.headers['Go-Defi-Device-Id']
        request.headers['Go-Defi-Device-Id-Signature'] = device.headers['Go-Defi-Device-Id-Signature']
        request.headers['Go-Defi-User-Agent'] = device.headers['Go-Defi-User-Agent']
      } catch (error) {
        console.error('Failed to create device info:', error)
      }
    }

    return request
  },
  (error) => Promise.reject(error),
)

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

let refreshInFlight: Promise<boolean> | null = null

export async function ensureFreshToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const user = getUser()
      if (!user?.refreshToken) return false

      try {
        const { data } = await axiosClient.post<AuthTokens>(ApiRoutes.AUTH_REFRESH_TOKEN, {
          refreshToken: user.refreshToken,
        })

        if (!getUser()) return false

        saveUser({ accessToken: data.accessToken, refreshToken: data.refreshToken })
        return true
      } catch {
        return false
      }
    })()
  }

  try {
    return await refreshInFlight
  } finally {
    refreshInFlight = null
  }
}

function forceSignOut() {
  cancelPendingRequests()
  clearAuthStorage()
  window.location.href = AppRoutes.AUTH_SIGNIN
}

export async function handleUnauthorized(error: AxiosError<any>) {
  const config = error.config as RetriableConfig | undefined

  if (!config || config.url === ApiRoutes.AUTH_REFRESH_TOKEN || config._retried) {
    forceSignOut()
    throw error
  }

  const refreshed = await ensureFreshToken()

  if (!refreshed) {
    forceSignOut()
    throw error
  }

  config._retried = true
  return axiosClient.request(config)
}

const createErrorInterceptor = (
  errorMap?: Record<number, (error: AxiosError<any>) => void>,
) => async (error: AxiosError<any>) => {
  if (typeof window === 'undefined') throw error

  if (error.response?.status === 401) {
    return handleUnauthorized(error)
  }

  if (error.response?.status && errorMap?.[error.response.status]) {
    errorMap[error.response.status](error)
  }

  throw error
}

axiosClient.interceptors.response.use(
  (response) => response,
  createErrorInterceptor(AXIOS_ERROR_HANDLER_MAP),
)
